import { PauseCircleIcon, XCircleIcon } from '@heroicons/react/24/solid';
import cn from 'classnames';
import { useMemo } from 'react';
import { CountryFlag } from 'shared/src/CountryFlag';
import { getCountryCode } from 'shared/src/getCountryCode';

import { virtualAccountNumberFormatter } from 'utils/formatters/virtualAccountNumberFormatter';
import { virtualAccountLabel } from 'utils/helpers';

import { AccountStatus } from 'api';

import type { VirtualAccount } from 'models/virtualAccount';

import { OrgNavLink } from 'components/ui/atoms/OrgNavLink';

export const AccountItem = ({
  accountInfo
}: {
  accountInfo: VirtualAccount;
}) => {
  const isSuspendedAccount = accountInfo.status === AccountStatus.suspended;
  const isTerminatedAccount = accountInfo.status === AccountStatus.terminated;
  const countryIsoCode = useMemo(
    () => getCountryCode(accountInfo.currency_code),
    [accountInfo.currency_code]
  );
  const statusIcon = isTerminatedAccount ? (
    <XCircleIcon
      className="size-5 shrink-0 text-content-strong"
      aria-label="Terminated account"
      title="Terminated account"
    />
  ) : isSuspendedAccount ? (
    <PauseCircleIcon
      className="size-5 shrink-0 text-feedback-attention"
      aria-label="Suspended account"
      title="Suspended account"
    />
  ) : null;

  return (
    <OrgNavLink
      key={accountInfo.account_number}
      to={`/accounts/${accountInfo.account_number}`}
      className={({ isActive }) =>
        cn(
          'group/item flex min-h-12 w-full flex-col justify-center rounded-full px-3 text-sm text-content-secondary',
          {
            'rounded-2xl bg-surface-selected text-content-brand':
              isActive && !isTerminatedAccount,
            'hover:bg-gray-100': !isActive && !isTerminatedAccount
          }
        )
      }
    >
      <div className="flex flex-row items-center gap-2">
        <div className="flex flex-col">
          <CountryFlag
            className={cn('!size-[22px]', {
              'opacity-40 grayscale': isTerminatedAccount
            })}
            countryCode={countryIsoCode}
            svg
          />
        </div>

        <div className="flex min-w-0 flex-1 flex-col overflow-hidden py-2">
          <div className="flex flex-row items-center gap-2 overflow-hidden">
            <span
              className={cn('truncate', {
                'text-content-subtle': isTerminatedAccount
              })}
            >
              {virtualAccountLabel(accountInfo)}
            </span>
          </div>

          <div
            className={cn('flex justify-between text-[.8rem]', {
              'text-content-subtle': isTerminatedAccount
            })}
          >
            <span className="truncate">
              No. {virtualAccountNumberFormatter(accountInfo.account_number)}
            </span>
          </div>
        </div>

        {statusIcon}
      </div>
    </OrgNavLink>
  );
};
